import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import _range from 'lodash/range';
import { ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import EmptyChannels from '@/assets/icons/empty-channels.svg';
import { Counter } from '@/components/ui/counter';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

import { IntegratedChannelsTableHeader } from './table-header';
import { IntegratedChannelsTableRow } from './table-row';

/* Layout component with all static content, to avoid code duplication in skeleton */
function IntegratedChannelsLayout({
  children,
  counter,
}: {
  children: ReactNode;
  counter: ReactNode;
}) {
  return (
    <div className="border-fx-neutral-200 flex flex-col rounded-md border bg-white">
      <div className="flex items-center justify-between p-4">
        {/* Title and description */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Typography variant="medium2" as="h2">
              <FormattedMessage
                defaultMessage="Integrated channels"
                description="Title for the Integrated channels"
              />
            </Typography>
            {counter}
          </div>
          <Typography variant="body1">
            <FormattedMessage
              defaultMessage="Integrated channels help you to post jobs, and track applications, spend data."
              description="Description for the Integrated channels"
            />
          </Typography>
        </div>
      </div>

      {children}
    </div>
  );
}

export function IntegratedChannels() {
  const intl = useIntl();

  const { data } = useSuspenseQuery({
    queryKey: [...queryKeys.settings.channels.integrated],
    queryFn: () =>
      SourcingService.getInternalApiSourcingSourcingChannelsAllChannels(),
  });

  // Filter channels to only include those that are either integratable or publishable
  const channels = useMemo(
    () =>
      data?.channels.filter(
        channel => channel.is_integratable || channel.is_publishable,
      ),
    [data],
  );

  return (
    <>
      <IntegratedChannelsLayout
        counter={channels.length > 0 && <Counter count={channels.length} />}
      >
        {/* Table headers */}
        <IntegratedChannelsTableHeader />

        {/* Empty state */}
        {channels.length === 0 && (
          <EmptyState
            className="w-full"
            icon={EmptyChannels}
            header={intl.formatMessage({
              defaultMessage: 'No channels configured yet!',
              description:
                'Empty state for the integrated (paid) channel table',
            })}
            description={intl.formatMessage({
              defaultMessage:
                'Create your first non-integrated channel to group UTM sources and track their performance.',
              description:
                'Empty-state body on channel settings encouraging first UTM-grouped channel setup',
            })}
          />
        )}

        {/* Table rows */}
        {channels.map(channel => (
          <IntegratedChannelsTableRow key={channel.slug} channel={channel} />
        ))}
      </IntegratedChannelsLayout>
    </>
  );
}

IntegratedChannels.Skeleton = function IntegratedChannelsSkeleton() {
  return (
    <IntegratedChannelsLayout
      counter={<Skeleton className="h-4 w-6 rounded-full" />}
    >
      <IntegratedChannelsTableHeader />

      {_range(3).map(index => (
        <IntegratedChannelsTableRow.Skeleton key={index} />
      ))}
    </IntegratedChannelsLayout>
  );
};
